import React, { useState } from 'react';

import { Carousel } from '@mantine/carousel';
import { Box, Flex, useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconArrowLeft, IconArrowRight } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import bgImage from 'assets/images/bg.png';
import { DiscoverySourceStep } from 'pages/Onboarding/OnboardingSteps/DiscoverySourceStep';
import { FinishStep } from 'pages/Onboarding/OnboardingSteps/FinishStep';
import { IndustrySelectionStep } from 'pages/Onboarding/OnboardingSteps/IndustrySelectionStep';
import { MessagingSmartAssistantsStep } from 'pages/Onboarding/OnboardingSteps/MessagingSmartAssistantsStep';
import { PhoneSmartAssistantsStep } from 'pages/Onboarding/OnboardingSteps/PhoneSmartAssistantsStep';
import { PrimaryInterestsSelectionStep } from 'pages/Onboarding/OnboardingSteps/PrimaryInterestsSelectionStep';
import { ResourcesStep } from 'pages/Onboarding/OnboardingSteps/ResourcesStep';
import { ShopifySmartAssistantsStep } from 'pages/Onboarding/OnboardingSteps/ShopifySmartAssistantStep';
import { WebsiteSmartAssistantsStep } from 'pages/Onboarding/OnboardingSteps/WebsiteSmartAssistantsStep';
import { WelcomeStep } from 'pages/Onboarding/OnboardingSteps/WelcomeStep';
import { OnboardingService } from 'services/onboarding-service';
import {
  OnboardingData,
  OnboardingIndustry,
  OnboardingLearnMoreInterestsData,
  OnboardingPrimaryInterest
} from 'shared/interfaces/Onboarding';

export function OnboardingPage() {
  const theme = useMantineTheme();
  const bellowMdBreakpoint = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);
  const [onboardingData, setOnboardingData] = useState<OnboardingData>({});

  const updateOnboardingLearnMoreInterestData = (data: Partial<OnboardingLearnMoreInterestsData>) =>
    setOnboardingData((prev) => ({
      ...prev,
      learnMoreInterests: { ...prev.learnMoreInterests, ...data }
    }));

  const updateOnboardingIndustry = (industry: OnboardingIndustry | string) =>
    setOnboardingData((prev) => ({
      ...prev,
      industry: industry || undefined
    }));

  const updateOnboardingPrimaryInterests = (primaryInterests: OnboardingPrimaryInterest[]) =>
    setOnboardingData((prev) => ({
      ...prev,
      primaryInterests: primaryInterests.length ? primaryInterests : undefined
    }));

  const updateOnboardingDiscoverySource = (discoverySource: string) =>
    setOnboardingData((prev) => ({
      ...prev,
      discoverySource: discoverySource || undefined
    }));

  const handleOnboardingDataMutation = useMutation({
    mutationFn: OnboardingService.handleOnboardingData
    // We don't handle success or errors
  });

  const submitOnboardingData = () => handleOnboardingDataMutation.mutate(onboardingData);

  const STEPS = [
    {
      component: <WelcomeStep />
    },
    {
      component: <ResourcesStep updateOnboardingLearnMoreInterestData={updateOnboardingLearnMoreInterestData} />
    },
    {
      component: (
        <WebsiteSmartAssistantsStep updateOnboardingLearnMoreInterestData={updateOnboardingLearnMoreInterestData} />
      )
    },
    {
      component: (
        <PhoneSmartAssistantsStep updateOnboardingLearnMoreInterestData={updateOnboardingLearnMoreInterestData} />
      )
    },
    {
      component: (
        <MessagingSmartAssistantsStep updateOnboardingLearnMoreInterestData={updateOnboardingLearnMoreInterestData} />
      )
    },
    {
      component: (
        <ShopifySmartAssistantsStep updateOnboardingLearnMoreInterestData={updateOnboardingLearnMoreInterestData} />
      )
    },
    {
      component: <IndustrySelectionStep updateOnboardingIndustry={updateOnboardingIndustry} />
    },
    {
      component: <PrimaryInterestsSelectionStep updateOnboardingPrimaryInterests={updateOnboardingPrimaryInterests} />
    },
    {
      component: <DiscoverySourceStep updateOnboardingDiscoverySource={updateOnboardingDiscoverySource} />
    },
    {
      component: <FinishStep submitOnboardingData={submitOnboardingData} />
    }
  ];

  return (
    <Flex
      direction="column"
      justify="center"
      align="center"
      mih="100svh"
      w="100vw"
      sx={{
        backgroundImage: `url(${bgImage})`,
        backgroundSize: bellowMdBreakpoint ? 'cover' : 'contain',
        backgroundRepeat: 'no-repeat',

        backgroundPosition: 'center'
      }}>
      <Carousel
        withIndicators
        withControls={!bellowMdBreakpoint}
        maw={{ base: '100%', md: '80%', lg: 1000 }}
        nextControlIcon={<IconArrowRight style={{ width: 30, height: 30 }} />}
        previousControlIcon={<IconArrowLeft style={{ width: 30, height: 30 }} />}
        styles={{
          indicators: {
            padding: 10
          },
          indicator: {
            backgroundColor: theme.colors.gray[6]
          },
          controls: {
            padding: 0
          },
          control: {
            '&[data-inactive]': {
              opacity: 0,
              cursor: 'default'
            },
            backgroundColor: 'transparent',
            border: 'none',
            boxShadow: 'none'
          }
        }}>
        {STEPS.map((step, index) => (
          <Carousel.Slide key={index}>
            <Box mx="auto" w={{ base: '100%', md: '80%' }} h={{ base: '100svh', md: 500 }} py={30}>
              {step.component}
            </Box>
          </Carousel.Slide>
        ))}
      </Carousel>
    </Flex>
  );
}
